import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, FileText, Trash2, Calendar, ArrowLeft, Shield, LogOut, Key, Loader2,
  Folder, FolderPlus, Users, Share2, MoreVertical, UserPlus, Settings, HelpCircle,
  Move, Copy, X, Check, Info, Library, ChevronRight, Search, Eye
} from 'lucide-react';
import { format } from 'date-fns';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cc23a1c1-e998-45d0-adb8-2e9c1c544201/artifacts/g7bc3tyb_image.png";

const PASSWORD_REQUIREMENTS = [
  { id: 'length', label: 'At least 8 characters', regex: /.{8,}/ },
  { id: 'uppercase', label: 'One uppercase letter (A-Z)', regex: /[A-Z]/ },
  { id: 'lowercase', label: 'One lowercase letter (a-z)', regex: /[a-z]/ },
  { id: 'digit', label: 'One digit (0-9)', regex: /\d/ },
  { id: 'special', label: 'One special character (!@#$%^&*)', regex: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/ }
];
const validatePassword = (password) => PASSWORD_REQUIREMENTS.every(req => req.regex.test(password));

const PasswordRequirements = ({ password }) => (
  <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
    <p className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1">
      <Info className="w-3 h-3" /> ISO 27001 Password Requirements
    </p>
    <div className="space-y-1">
      {PASSWORD_REQUIREMENTS.map(req => {
        const isValid = req.regex.test(password);
        return (
          <div key={req.id} className="flex items-center gap-2 text-xs">
            {isValid ? <Check className="w-3 h-3 text-green-500" /> : <X className="w-3 h-3 text-slate-300" />}
            <span className={isValid ? 'text-green-700' : 'text-slate-500'}>{req.label}</span>
          </div>
        );
      })}
    </div>
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdminOrLead = user?.role === 'admin' || user?.role === 'lead';

  // Data
  const [projects, setProjects] = useState([]);
  const [sharedProjects, setSharedProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [teams, setTeams] = useState([]);
  const [libraryFolders, setLibraryFolders] = useState([]);
  const [libraryProjects, setLibraryProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Navigation
  const [activeTab, setActiveTab] = useState('my-projects');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [libraryPath, setLibraryPath] = useState([]); // breadcrumb: [{id, name}]
  const [currentLibFolderId, setCurrentLibFolderId] = useState(null);

  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [showTeamMemberDialog, setShowTeamMemberDialog] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [showLibFolderDialog, setShowLibFolderDialog] = useState(false);
  const [projectToMove, setProjectToMove] = useState(null);

  // Forms
  const [newProjectName, setNewProjectName] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDesc, setNewTeamDesc] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('viewer');
  const [selectedTeamForMember, setSelectedTeamForMember] = useState(null);
  const [creating, setCreating] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [newLibFolderName, setNewLibFolderName] = useState('');

  // Create user form
  const [newUserData, setNewUserData] = useState({ email: '', full_name: '', password: '', role: 'user' });
  const [creatingUser, setCreatingUser] = useState(false);

  // User search for team member addition
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);

  // Fetch main data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let projectsUrl = `${API}/projects`;
      if (selectedFolder) projectsUrl += `?folder_id=${selectedFolder}`;
      else if (selectedTeam) projectsUrl += `?team_id=${selectedTeam}`;

      const [projectsRes, sharedRes, foldersRes, teamsRes] = await Promise.all([
        axios.get(projectsUrl),
        axios.get(`${API}/projects?shared_only=true`),
        axios.get(`${API}/folders`),
        axios.get(`${API}/teams`)
      ]);
      setProjects(projectsRes.data);
      setSharedProjects(sharedRes.data);
      setFolders(foldersRes.data);
      setTeams(teamsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [selectedFolder, selectedTeam]);

  // Fetch library data
  const fetchLibrary = useCallback(async (parentId = null) => {
    try {
      let url = `${API}/folders?folder_type=library`;
      if (parentId) url += `&parent_id=${parentId}`;
      const [foldersRes, projectsRes] = await Promise.all([
        axios.get(url),
        parentId ? axios.get(`${API}/library/projects?folder_id=${parentId}`) : axios.get(`${API}/library/projects`)
      ]);
      setLibraryFolders(foldersRes.data);
      if (parentId) {
        setLibraryProjects(projectsRes.data);
      } else {
        setLibraryProjects(projectsRes.data);
      }
    } catch (error) {
      console.error('Error fetching library:', error);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (activeTab === 'library') fetchLibrary(currentLibFolderId);
  }, [activeTab, currentLibFolderId, fetchLibrary]);

  // User search debounce
  useEffect(() => {
    if (!newMemberEmail || newMemberEmail.length < 2) {
      setUserSearchResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setSearchingUsers(true);
      try {
        const res = await axios.get(`${API}/users/search?query=${encodeURIComponent(newMemberEmail)}`);
        setUserSearchResults(res.data);
      } catch (e) { /* ignore */ }
      setSearchingUsers(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [newMemberEmail]);

  // === Actions ===
  const createProject = async () => {
    if (!newProjectName.trim()) return toast.error('Please enter a project name');
    setCreating(true);
    try {
      const payload = { name: newProjectName, folder_id: selectedFolder, team_id: selectedTeam };
      // If in library tab, set folder_id to current library folder
      if (activeTab === 'library' && currentLibFolderId) {
        payload.folder_id = currentLibFolderId;
        payload.team_id = null;
      }
      const response = await axios.post(`${API}/projects`, payload);
      toast.success('Project created');
      setShowCreateDialog(false);
      setNewProjectName('');
      navigate(`/editor/${response.data.id}`);
    } catch (error) { toast.error('Failed to create project'); }
    finally { setCreating(false); }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return toast.error('Please enter a folder name');
    try {
      await axios.post(`${API}/folders`, { name: newFolderName, team_id: selectedTeam });
      toast.success('Folder created');
      setShowFolderDialog(false);
      setNewFolderName('');
      fetchData();
    } catch (error) { toast.error('Failed to create folder'); }
  };

  const createLibraryFolder = async () => {
    if (!newLibFolderName.trim()) return toast.error('Please enter a folder name');
    try {
      await axios.post(`${API}/folders`, {
        name: newLibFolderName,
        folder_type: 'library',
        parent_id: currentLibFolderId
      });
      toast.success('Library folder created');
      setShowLibFolderDialog(false);
      setNewLibFolderName('');
      fetchLibrary(currentLibFolderId);
    } catch (error) { toast.error('Failed to create folder'); }
  };

  const createTeam = async () => {
    if (!newTeamName.trim()) return toast.error('Please enter a team name');
    try {
      await axios.post(`${API}/teams`, { name: newTeamName, description: newTeamDesc });
      toast.success('Team created');
      setShowTeamDialog(false);
      setNewTeamName('');
      setNewTeamDesc('');
      fetchData();
    } catch (error) { toast.error('Failed to create team'); }
  };

  const addTeamMember = async () => {
    if (!newMemberEmail.trim() || !selectedTeamForMember) return toast.error('Please enter an email');
    try {
      await axios.post(`${API}/teams/${selectedTeamForMember}/members`, { email: newMemberEmail, role: newMemberRole });
      toast.success('Member added');
      setNewMemberEmail('');
      setNewMemberRole('viewer');
      setUserSearchResults([]);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add member'); }
  };

  const removeTeamMember = async (teamId, userId) => {
    if (!window.confirm('Remove this member from the team?')) return;
    try {
      await axios.delete(`${API}/teams/${teamId}/members/${userId}`);
      toast.success('Member removed');
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to remove member'); }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success('Project deleted');
      fetchData();
      if (activeTab === 'library') fetchLibrary(currentLibFolderId);
    } catch (error) { toast.error('Failed to delete project'); }
  };

  const deleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder and all subfolders? Projects will be moved out.')) return;
    try {
      await axios.delete(`${API}/folders/${folderId}`);
      toast.success('Folder deleted');
      setSelectedFolder(null);
      fetchData();
      if (activeTab === 'library') fetchLibrary(currentLibFolderId);
    } catch (error) { toast.error('Failed to delete folder'); }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) return toast.error('Passwords do not match');
    if (!validatePassword(passwordData.newPassword)) return toast.error('Password does not meet requirements');
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: passwordData.currentPassword, new_password: passwordData.newPassword });
      toast.success('Password changed');
      setShowPasswordDialog(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to change password'); }
    finally { setChangingPassword(false); }
  };

  const createUser = async () => {
    if (!newUserData.email || !newUserData.full_name || !newUserData.password) return toast.error('All fields required');
    if (!validatePassword(newUserData.password)) return toast.error('Password does not meet ISO 27001 requirements');
    setCreatingUser(true);
    try {
      await axios.post(`${API}/admin/users`, newUserData);
      toast.success(`User ${newUserData.email} created`);
      setShowCreateUserDialog(false);
      setNewUserData({ email: '', full_name: '', password: '', role: 'user' });
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create user'); }
    finally { setCreatingUser(false); }
  };

  const moveProject = async (projectId, folderId) => {
    try {
      await axios.put(`${API}/projects/${projectId}/move`, { folder_id: folderId });
      toast.success('Project moved');
      setShowMoveDialog(false);
      setProjectToMove(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to move project'); }
  };

  const moveToTeam = async (projectId, teamId) => {
    try {
      await axios.put(`${API}/projects/${projectId}/move`, { team_id: teamId });
      toast.success('Project moved to team');
      setShowMoveDialog(false);
      setProjectToMove(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to move project'); }
  };

  const copyProject = async (projectId, folderId = null, teamId = null) => {
    try {
      await axios.post(`${API}/projects/${projectId}/copy`, { folder_id: folderId, team_id: teamId });
      toast.success('Project copied');
      setShowMoveDialog(false);
      setProjectToMove(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to copy project'); }
  };

  const navigateToLibFolder = (folder) => {
    setLibraryPath(prev => [...prev, { id: folder.id, name: folder.name }]);
    setCurrentLibFolderId(folder.id);
  };

  const navigateLibBreadcrumb = (index) => {
    if (index < 0) {
      setLibraryPath([]);
      setCurrentLibFolderId(null);
    } else {
      const newPath = libraryPath.slice(0, index + 1);
      setLibraryPath(newPath);
      setCurrentLibFolderId(newPath[newPath.length - 1].id);
    }
  };

  const clearSelection = () => {
    setSelectedFolder(null);
    setSelectedTeam(null);
    setActiveTab('my-projects');
  };

  // === Components ===
  const ProjectCard = ({ project, isShared = false, isLibrary = false }) => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="h-full">
      <Card className="h-full p-5 border border-slate-200 shadow-sm hover:shadow-md hover:border-primary/30 cursor-pointer group transition-all duration-200 flex flex-col">
        <div data-testid={`project-card-${project.id}`} onClick={() => navigate(`/editor/${project.id}`)} className="flex flex-col h-full">
          <div className="flex items-start justify-between mb-4">
            <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${isShared ? 'bg-green-50' : isLibrary ? 'bg-amber-50' : 'bg-blue-50'}`}>
              {isShared ? <Share2 className="w-5 h-5 text-green-600" /> : isLibrary ? <Eye className="w-5 h-5 text-amber-600" /> : <FileText className="w-5 h-5 text-primary" />}
            </div>
            {!isShared && !isLibrary && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 h-8 w-8 p-0" onClick={(e) => e.stopPropagation()}>
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/editor/${project.id}?share=true`); }}>
                    <Share2 className="w-4 h-4 mr-2" /> Share
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setProjectToMove({...project, action: 'move'}); setShowMoveDialog(true); }}>
                    <Move className="w-4 h-4 mr-2" /> Move to...
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setProjectToMove({...project, action: 'copy'}); setShowMoveDialog(true); }}>
                    <Copy className="w-4 h-4 mr-2" /> Copy to...
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-red-600" onClick={(e) => { e.stopPropagation(); deleteProject(project.id); }}>
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {isLibrary && isAdminOrLead && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 h-8 w-8 p-0" onClick={(e) => e.stopPropagation()}>
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem className="text-red-600" onClick={(e) => { e.stopPropagation(); deleteProject(project.id); }}>
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          <h3 className="text-base font-semibold text-slate-900 mb-2 group-hover:text-primary transition-colors line-clamp-2 min-h-[48px]">
            {project.title || project.name}
          </h3>
          {isShared && <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full w-fit mb-2"><Share2 className="w-3 h-3" /> Shared with you</span>}
          {isLibrary && <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full w-fit mb-2"><Eye className="w-3 h-3" /> View Only</span>}
          <div className="flex-1" />
          <div className="pt-3 border-t border-slate-100 mt-3">
            <div className="flex items-center justify-between text-sm text-slate-500">
              <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /><span>{project.created_at ? format(new Date(project.created_at), 'MMM dd, yyyy') : 'N/A'}</span></div>
              {project.steps?.length > 0 && <span className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium">{project.steps.length} steps</span>}
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );

  const FolderCard = ({ folder, onSelect, isSelected }) => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <Card
        data-testid={`folder-card-${folder.id}`}
        className={`p-4 border cursor-pointer group hover:border-primary transition-colors ${isSelected ? 'border-primary bg-blue-50' : 'border-slate-200'}`}
        onClick={() => onSelect(folder)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Folder className={`w-5 h-5 ${isSelected ? 'text-primary' : 'text-slate-400'}`} />
            <span className="font-medium text-slate-800">{folder.name}</span>
          </div>
          {(folder.owner_id === user?.id || user?.role === 'admin') && (
            <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); deleteFolder(folder.id); }}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          )}
        </div>
      </Card>
    </motion.div>
  );

  const LibraryFolderCard = ({ folder }) => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <Card
        data-testid={`lib-folder-${folder.id}`}
        className="p-4 border border-amber-200 cursor-pointer group hover:border-amber-400 hover:shadow-sm transition-all bg-amber-50/30"
        onClick={() => navigateToLibFolder(folder)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Folder className="w-5 h-5 text-amber-500" />
            <span className="font-medium text-slate-800">{folder.name}</span>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </div>
          {isAdminOrLead && (
            <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); deleteFolder(folder.id); }}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          )}
        </div>
      </Card>
    </motion.div>
  );

  const TeamCard = ({ team }) => {
    const userRole = team.members?.find(m => m.user_id === user?.id)?.role || 'viewer';
    const isTeamAdmin = userRole === 'admin';
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Card
          data-testid={`team-card-${team.id}`}
          className={`p-4 border cursor-pointer group hover:border-primary transition-colors ${selectedTeam === team.id ? 'border-primary bg-blue-50' : 'border-slate-200'}`}
          onClick={() => { setSelectedTeam(selectedTeam === team.id ? null : team.id); setSelectedFolder(null); setActiveTab('my-projects'); }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className={`w-5 h-5 ${selectedTeam === team.id ? 'text-primary' : 'text-slate-400'}`} />
              <div>
                <span className="font-medium text-slate-800">{team.name}</span>
                <p className="text-xs text-slate-500">{team.members?.length || 0} members</p>
              </div>
            </div>
            {isTeamAdmin && (
              <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); setSelectedTeamForMember(team.id); setShowTeamMemberDialog(true); }}>
                <UserPlus className="w-4 h-4" />
              </Button>
            )}
          </div>
        </Card>
      </motion.div>
    );
  };

  const EmptyState = ({ icon: Icon, title, subtitle, action }) => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-24">
      <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6">
        <Icon className="w-12 h-12 text-slate-400" />
      </div>
      <h3 className="text-xl font-semibold text-slate-900 mb-2">{title}</h3>
      <p className="text-slate-600 mb-6">{subtitle}</p>
      {action}
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="FlowSnap" className="h-14" />
          </div>
          <div className="flex items-center gap-3">
            <Button data-testid="help-btn" variant="ghost" onClick={() => navigate('/help')} className="text-slate-600 hover:text-primary">
              <HelpCircle className="w-4 h-4 mr-2" /> Help
            </Button>
            {user?.role === 'admin' && (
              <Button data-testid="admin-panel-btn" variant="outline" onClick={() => navigate('/admin')} className="border-red-200 text-red-600 hover:bg-red-50">
                <Shield className="w-4 h-4 mr-2" /> Admin Panel
              </Button>
            )}
            <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
              <DialogTrigger asChild>
                <Button data-testid="change-password-btn" variant="outline" className="border-slate-300">
                  <Key className="w-4 h-4 mr-2" /> Change Password
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Change Password</DialogTitle><DialogDescription>Enter your current password and a new password.</DialogDescription></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div><label className="block text-sm font-medium text-slate-700 mb-2">Current Password</label><Input data-testid="current-password-input" type="password" value={passwordData.currentPassword} onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})} /></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-2">New Password</label><Input data-testid="new-password-input" type="password" value={passwordData.newPassword} onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})} /><PasswordRequirements password={passwordData.newPassword} /></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-2">Confirm New Password</label><Input data-testid="confirm-password-input" type="password" value={passwordData.confirmPassword} onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})} /></div>
                  <div className="flex gap-2 justify-end pt-4">
                    <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>Cancel</Button>
                    <Button data-testid="change-password-submit-btn" onClick={handleChangePassword} disabled={changingPassword || !validatePassword(passwordData.newPassword)} className="bg-primary text-white hover:bg-blue-700">
                      {changingPassword ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Changing...</> : 'Change Password'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Button data-testid="logout-btn" variant="outline" onClick={() => { logout(); navigate('/login'); }} className="border-slate-300">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className="w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-6">
              {/* Action Buttons */}
              <div className="space-y-2">
                <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-project-btn" className="w-full bg-primary text-white hover:bg-blue-700"><Plus className="w-4 h-4 mr-2" /> New Project</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Create New Project</DialogTitle><DialogDescription>Enter a name for your new documentation project</DialogDescription></DialogHeader>
                    <div className="space-y-4 mt-4">
                      <Input data-testid="project-name-input" placeholder="Enter project name..." value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && createProject()} />
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                        <Button data-testid="create-project-submit-btn" onClick={createProject} disabled={creating} className="bg-primary text-white hover:bg-blue-700">{creating ? 'Creating...' : 'Create'}</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                {isAdminOrLead && (
                  <>
                    <Dialog open={showFolderDialog} onOpenChange={setShowFolderDialog}>
                      <DialogTrigger asChild><Button variant="outline" className="w-full"><FolderPlus className="w-4 h-4 mr-2" /> New Folder</Button></DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Create Folder</DialogTitle><DialogDescription>Organize your projects into folders</DialogDescription></DialogHeader>
                        <div className="space-y-4 mt-4">
                          <Input placeholder="Folder name..." value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && createFolder()} />
                          <div className="flex gap-2 justify-end"><Button variant="outline" onClick={() => setShowFolderDialog(false)}>Cancel</Button><Button onClick={createFolder} className="bg-primary text-white">Create</Button></div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Dialog open={showTeamDialog} onOpenChange={setShowTeamDialog}>
                      <DialogTrigger asChild><Button variant="outline" className="w-full"><Users className="w-4 h-4 mr-2" /> New Team</Button></DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Create Team</DialogTitle><DialogDescription>Create a team workspace for collaboration</DialogDescription></DialogHeader>
                        <div className="space-y-4 mt-4">
                          <Input placeholder="Team name..." value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} />
                          <Input placeholder="Description (optional)..." value={newTeamDesc} onChange={(e) => setNewTeamDesc(e.target.value)} />
                          <div className="flex gap-2 justify-end"><Button variant="outline" onClick={() => setShowTeamDialog(false)}>Cancel</Button><Button onClick={createTeam} className="bg-primary text-white">Create</Button></div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
                      <DialogTrigger asChild><Button variant="outline" className="w-full"><UserPlus className="w-4 h-4 mr-2" /> Create User</Button></DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Create New User</DialogTitle><DialogDescription>Create an account for a team member</DialogDescription></DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div><label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label><Input data-testid="new-user-name" placeholder="John Doe" value={newUserData.full_name} onChange={(e) => setNewUserData({...newUserData, full_name: e.target.value})} /></div>
                          <div><label className="block text-sm font-medium text-slate-700 mb-1">Email</label><Input data-testid="new-user-email" type="email" placeholder="user@company.com" value={newUserData.email} onChange={(e) => setNewUserData({...newUserData, email: e.target.value})} /></div>
                          <div><label className="block text-sm font-medium text-slate-700 mb-1">Password</label><Input data-testid="new-user-password" type="password" placeholder="Secure password" value={newUserData.password} onChange={(e) => setNewUserData({...newUserData, password: e.target.value})} /><PasswordRequirements password={newUserData.password} /></div>
                          {user?.role === 'admin' && (
                            <div><label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                              <select data-testid="new-user-role" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={newUserData.role} onChange={(e) => setNewUserData({...newUserData, role: e.target.value})}>
                                <option value="user">User</option><option value="lead">Lead (Team Owner)</option><option value="admin">Admin</option>
                              </select>
                            </div>
                          )}
                          <div className="flex gap-2 justify-end pt-2">
                            <Button variant="outline" onClick={() => setShowCreateUserDialog(false)}>Cancel</Button>
                            <Button data-testid="create-user-submit-btn" onClick={createUser} disabled={creatingUser} className="bg-primary text-white">{creatingUser ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : 'Create User'}</Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
              </div>

              {/* My Folders */}
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">{selectedTeam ? 'Team Folders' : 'My Folders'}</h3>
                <div className="space-y-2">
                  {folders.filter(f => f.folder_type !== 'library').map(folder => <FolderCard key={folder.id} folder={folder} isSelected={selectedFolder === folder.id} onSelect={(f) => setSelectedFolder(selectedFolder === f.id ? null : f.id)} />)}
                  {folders.filter(f => f.folder_type !== 'library').length === 0 && <p className="text-sm text-slate-400 px-2">No folders yet</p>}
                </div>
              </div>

              {/* Teams */}
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Teams</h3>
                <div className="space-y-2">
                  {teams.map(team => <TeamCard key={team.id} team={team} />)}
                  {teams.length === 0 && <p className="text-sm text-slate-400 px-2">No teams yet</p>}
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            <div className="mb-6">
              {(selectedTeam || selectedFolder) && (
                <Button variant="ghost" onClick={clearSelection} className="text-slate-600 hover:text-primary -ml-2 mb-2">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to All Projects
                </Button>
              )}
              <h2 className="text-3xl font-bold text-slate-900 mb-2">
                {selectedTeam ? teams.find(t => t.id === selectedTeam)?.name : selectedFolder ? folders.find(f => f.id === selectedFolder)?.name : 'My Workspace'}
              </h2>
              <p className="text-slate-600">Welcome back, {user?.full_name || 'User'}!</p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="mb-6">
                <TabsTrigger data-testid="tab-my-projects" value="my-projects" className="px-6"><FileText className="w-4 h-4 mr-2" />{selectedTeam ? 'Team Projects' : 'My Projects'}</TabsTrigger>
                {!selectedTeam && <TabsTrigger data-testid="tab-shared" value="shared" className="px-6"><Share2 className="w-4 h-4 mr-2" />Shared{sharedProjects.length > 0 && <span className="ml-2 bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">{sharedProjects.length}</span>}</TabsTrigger>}
                <TabsTrigger data-testid="tab-library" value="library" className="px-6"><Library className="w-4 h-4 mr-2" />Shared Library</TabsTrigger>
              </TabsList>

              {/* My Projects Tab */}
              <TabsContent value="my-projects">
                {loading ? (
                  <div className="flex items-center justify-center py-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>
                ) : projects.length === 0 ? (
                  <EmptyState icon={FileText} title="No projects yet" subtitle="Create your first project to get started" action={<Button data-testid="create-first-project-btn" onClick={() => setShowCreateDialog(true)} className="bg-primary text-white hover:bg-blue-700"><Plus className="w-4 h-4 mr-2" />Create Project</Button>} />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-fr">
                    {projects.map(p => <ProjectCard key={p.id} project={p} />)}
                  </div>
                )}
              </TabsContent>

              {/* Shared Tab */}
              <TabsContent value="shared">
                {loading ? (
                  <div className="flex items-center justify-center py-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>
                ) : sharedProjects.length === 0 ? (
                  <EmptyState icon={Share2} title="No shared projects" subtitle="Projects shared with you will appear here" />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-fr">
                    {sharedProjects.map(p => <ProjectCard key={p.id} project={p} isShared />)}
                  </div>
                )}
              </TabsContent>

              {/* Shared Library Tab */}
              <TabsContent value="library">
                {/* Breadcrumbs */}
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <button data-testid="lib-breadcrumb-root" onClick={() => navigateLibBreadcrumb(-1)} className={`text-sm font-medium hover:text-primary transition-colors ${libraryPath.length === 0 ? 'text-primary' : 'text-slate-500'}`}>
                    Shared Library
                  </button>
                  {libraryPath.map((crumb, i) => (
                    <React.Fragment key={crumb.id}>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                      <button data-testid={`lib-breadcrumb-${i}`} onClick={() => navigateLibBreadcrumb(i)} className={`text-sm font-medium hover:text-primary transition-colors ${i === libraryPath.length - 1 ? 'text-primary' : 'text-slate-500'}`}>
                        {crumb.name}
                      </button>
                    </React.Fragment>
                  ))}
                </div>

                {/* Create Library Folder button (admin/lead only) */}
                {isAdminOrLead && (
                  <div className="flex gap-2 mb-6">
                    <Dialog open={showLibFolderDialog} onOpenChange={setShowLibFolderDialog}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" data-testid="create-lib-folder-btn"><FolderPlus className="w-4 h-4 mr-2" /> New Folder</Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Create Library Folder</DialogTitle><DialogDescription>{currentLibFolderId ? `Creating subfolder in: ${libraryPath[libraryPath.length - 1]?.name}` : 'Create a root-level shared folder'}</DialogDescription></DialogHeader>
                        <div className="space-y-4 mt-4">
                          <Input data-testid="lib-folder-name-input" placeholder="Folder name..." value={newLibFolderName} onChange={(e) => setNewLibFolderName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && createLibraryFolder()} />
                          <div className="flex gap-2 justify-end"><Button variant="outline" onClick={() => setShowLibFolderDialog(false)}>Cancel</Button><Button data-testid="create-lib-folder-submit" onClick={createLibraryFolder} className="bg-primary text-white">Create</Button></div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    {currentLibFolderId && (
                      <Button variant="outline" size="sm" onClick={() => setShowCreateDialog(true)} data-testid="add-project-to-lib-btn">
                        <Plus className="w-4 h-4 mr-2" /> Add Project Here
                      </Button>
                    )}
                  </div>
                )}

                {/* Library Folders */}
                {libraryFolders.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Folders</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {libraryFolders.map(f => <LibraryFolderCard key={f.id} folder={f} />)}
                    </div>
                  </div>
                )}

                {/* Library Projects */}
                {libraryProjects.length > 0 ? (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Documents & Videos</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-fr">
                      {libraryProjects.map(p => <ProjectCard key={p.id} project={p} isLibrary />)}
                    </div>
                  </div>
                ) : libraryFolders.length === 0 ? (
                  <EmptyState icon={Library} title="Shared Library is empty" subtitle={isAdminOrLead ? "Create folders and add documents for your team" : "Your admin will add training materials here"} action={isAdminOrLead ? <Button onClick={() => setShowLibFolderDialog(true)} className="bg-primary text-white"><FolderPlus className="w-4 h-4 mr-2" />Create First Folder</Button> : null} />
                ) : null}
              </TabsContent>
            </Tabs>
          </main>
        </div>
      </div>

      {/* Manage Team Members Dialog */}
      <Dialog open={showTeamMemberDialog} onOpenChange={setShowTeamMemberDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Manage Team Members</DialogTitle><DialogDescription>Add or remove members from this team</DialogDescription></DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedTeamForMember && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Current Members</label>
                <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-md divide-y">
                  {teams.find(t => t.id === selectedTeamForMember)?.members?.map((member) => (
                    <div key={member.user_id} className="flex items-center justify-between p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center"><Users className="w-4 h-4 text-primary" /></div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{member.email || member.user_id}</p>
                          <span className={`text-xs px-2 py-0.5 rounded ${member.role === 'admin' ? 'bg-red-100 text-red-700' : member.role === 'editor' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{member.role}</span>
                        </div>
                      </div>
                      {member.role !== 'admin' && (
                        <Button variant="ghost" size="sm" onClick={() => removeTeamMember(selectedTeamForMember, member.user_id)} className="text-red-500 hover:bg-red-50"><X className="w-4 h-4" /></Button>
                      )}
                    </div>
                  )) || <p className="p-3 text-sm text-slate-500">No members yet</p>}
                </div>
              </div>
            )}
            <div className="pt-4 border-t border-slate-200">
              <label className="block text-sm font-medium text-slate-700 mb-2">Add New Member</label>
              <div className="relative">
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        data-testid="team-member-search"
                        placeholder="Search by email..."
                        value={newMemberEmail}
                        onChange={(e) => setNewMemberEmail(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    {/* Search dropdown */}
                    {userSearchResults.length > 0 && newMemberEmail.length >= 2 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-40 overflow-y-auto">
                        {userSearchResults.map(u => (
                          <button
                            key={u.id}
                            data-testid={`user-result-${u.id}`}
                            className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50 transition-colors"
                            onClick={() => { setNewMemberEmail(u.email); setUserSearchResults([]); }}
                          >
                            <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center"><Users className="w-3 h-3 text-primary" /></div>
                            <div>
                              <p className="font-medium">{u.full_name}</p>
                              <p className="text-xs text-slate-500">{u.email} · {u.role}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <select className="border border-slate-300 rounded-md px-3 py-2 text-sm" value={newMemberRole} onChange={(e) => setNewMemberRole(e.target.value)}>
                    <option value="viewer">Viewer</option><option value="editor">Editor</option><option value="admin">Admin</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => { setShowTeamMemberDialog(false); setUserSearchResults([]); }}>Close</Button>
              <Button data-testid="add-member-btn" onClick={addTeamMember} className="bg-primary text-white" disabled={!newMemberEmail.trim()}><UserPlus className="w-4 h-4 mr-2" />Add Member</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move/Copy Project Dialog */}
      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{projectToMove?.action === 'copy' ? 'Copy' : 'Move'} Project</DialogTitle><DialogDescription>{projectToMove?.action === 'copy' ? 'Copy' : 'Move'} "{projectToMove?.title || projectToMove?.name}"</DialogDescription></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">To Folder</h4>
              <div className="space-y-1 max-h-32 overflow-y-auto border border-slate-200 rounded-md p-2">
                <button onClick={() => projectToMove?.action === 'copy' ? copyProject(projectToMove?.id) : moveProject(projectToMove?.id, null)} className="w-full flex items-center gap-2 p-2 text-left text-sm hover:bg-slate-100 rounded-md"><X className="w-4 h-4 text-slate-400" /><span>No folder (My Workspace)</span></button>
                {folders.map(folder => (
                  <button key={folder.id} onClick={() => projectToMove?.action === 'copy' ? copyProject(projectToMove?.id, folder.id) : moveProject(projectToMove?.id, folder.id)} className={`w-full flex items-center gap-2 p-2 text-left text-sm hover:bg-slate-100 rounded-md ${projectToMove?.folder_id === folder.id ? 'bg-blue-50 text-primary' : ''}`}>
                    <Folder className="w-4 h-4 text-slate-400" /><span>{folder.name}</span>{projectToMove?.folder_id === folder.id && <span className="text-xs text-slate-400 ml-auto">(current)</span>}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">To Team</h4>
              <div className="space-y-1 max-h-32 overflow-y-auto border border-slate-200 rounded-md p-2">
                <button onClick={() => projectToMove?.action === 'copy' ? copyProject(projectToMove?.id) : moveToTeam(projectToMove?.id, null)} className="w-full flex items-center gap-2 p-2 text-left text-sm hover:bg-slate-100 rounded-md"><X className="w-4 h-4 text-slate-400" /><span>No team (Personal)</span></button>
                {teams.map(team => (
                  <button key={team.id} onClick={() => projectToMove?.action === 'copy' ? copyProject(projectToMove?.id, null, team.id) : moveToTeam(projectToMove?.id, team.id)} className={`w-full flex items-center gap-2 p-2 text-left text-sm hover:bg-slate-100 rounded-md ${projectToMove?.team_id === team.id ? 'bg-blue-50 text-primary' : ''}`}>
                    <Users className="w-4 h-4 text-slate-400" /><span>{team.name}</span>{projectToMove?.team_id === team.id && <span className="text-xs text-slate-400 ml-auto">(current)</span>}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2"><Button variant="outline" onClick={() => { setShowMoveDialog(false); setProjectToMove(null); }}>Cancel</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
